import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

// Shared scroll-lock state across all dialog and details consumers.
// Counter prevents nested dialogs from prematurely releasing the lock.
let scrollLockCount = 0;

// Scrollbar width measured at first lock acquisition. 0 on overlay-scrollbar platforms.
let scrollLockScrollbarWidth = 0;

// Scroll position captured at the first lock acquisition; restored at the last release.
let scrollLockScrollY = 0;

/**
 * Acquires a scroll lock.
 * Classic scrollbars (width > 0): overflow:hidden + padding-right on <html>.
 * Overlay scrollbars (width === 0): position:fixed on body; scroll position saved at module scope.
 */
function acquireScrollLock() {
  if (scrollLockCount === 0) {
    // Use getBoundingClientRect for sub-pixel precision; clientWidth rounds to integer.
    scrollLockScrollbarWidth = window.innerWidth - document.documentElement.getBoundingClientRect().width;
    scrollLockScrollY = window.scrollY;

    if (scrollLockScrollbarWidth > 0) {
      document.documentElement.style.setProperty('--scrollbar-width', `${scrollLockScrollbarWidth}px`);
      document.documentElement.style.overflow = 'hidden';
      document.documentElement.style.paddingRight = `${scrollLockScrollbarWidth}px`;
    } else {
      // Overlay scrollbars (iOS/macOS): position:fixed is the only reliable scroll lock.
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollLockScrollY}px`;
    }
  }
  scrollLockCount++;
}

/**
 * Releases a scroll lock.
 * On the last call (count 1 → 0) removes styles applied by acquireScrollLock.
 */
function releaseScrollLock() {
  scrollLockCount--;
  if (scrollLockCount === 0) {
    if (scrollLockScrollbarWidth > 0) {
      document.documentElement.style.overflow = '';
      document.documentElement.style.paddingRight = '';
      document.documentElement.style.removeProperty('--scrollbar-width');
    } else {
      // Overlay path: restore body styles and scroll position.
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: scrollLockScrollY, behavior: 'instant' });
    }
    scrollLockScrollbarWidth = 0;
    scrollLockScrollY = 0;
  }
}

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  /**
   * The dialog element this component owns.
   *
   * `this.refs.dialog` can be polluted: when markup is morphed into the dialog (e.g.
   * the quick-add product content) that contains nested custom elements declaring
   * their own `ref="dialog"`, and such an element has not upgraded yet (its script
   * isn't loaded — e.g. the UK-only find-in-store / zoom drawers), the framework
   * cannot tell the ref belongs to a nested component, so it leaks into this
   * component's refs and — last write wins — overwrites the real dialog with a nested
   * one (often already open), which makes `showDialog()` early-return on `dialog.open`
   * and the modal never appears. The component's own dialog is always a direct child
   * of the host, so prefer that and fall back to `refs.dialog` for any layout where
   * the dialog isn't a direct child.
   * @returns {HTMLDialogElement}
   */
  get ownDialog() {
    return /** @type {HTMLDialogElement} */ (this.querySelector(':scope > dialog')) ?? this.refs.dialog;
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /**
   * Shows the dialog.
   */
  showDialog() {
    const dialog = this.ownDialog;

    if (dialog.open) return;

    requestAnimationFrame(() => {
      // Show the modal BEFORE acquiring the scroll lock. If showModal() throws
      // (e.g. the dialog has been detached from the document), acquiring the
      // lock first would leave the body scroll-locked (position:fixed) with no
      // visible modal — a frozen, unclickable page. Locking only after a
      // successful open guarantees the lock is always paired with a shown dialog.
      try {
        dialog.showModal();
      } catch (error) {
        console.error('[dialog] showModal failed; scroll lock not acquired', error);
        return;
      }

      acquireScrollLock();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const dialog = this.ownDialog;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    dialog.style.animation = 'none';
    void dialog.offsetWidth;
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    releaseScrollLock();

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.ownDialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Overridable: returns false to prevent close-on-outside-click (e.g. for nested dialogs).
   * @param {MouseEvent} event - The mouse event.
   * @returns {boolean}
   */
  shouldCloseOnOutsideClick(event) {
    return true;
  }

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    if (!this.shouldCloseOnOutsideClick(event)) return;

    const dialog = this.ownDialog;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        if (event.target.open) {
          acquireScrollLock();
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
          releaseScrollLock();
        }
      }
    }
  },
  { capture: true }
);
